import React, { useCallback } from 'react';
import { gql, useMutation } from '@apollo/client';
import { IconButton, Tooltip } from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

const archiveEventsMutation = gql`
  mutation ArchiveInboxEvent($input: ArchiveEventsInput!) {
    archiveEvents(input: $input) {
      events {
        id
      }
      errors {
        message
      }
    }
  }
`;

const unarchiveEventsMutation = gql`
  mutation UnarchiveInboxEvent($input: UnarchiveEventsInput!) {
    unarchiveEvents(input: $input) {
      events {
        id
      }
      errors {
        message
      }
    }
  }
`;

export const InboxArchiveButton = ({
  eventId,
  archived,
  onComplete,
}: {
  eventId: string;
  archived: boolean;
  onComplete?: () => void;
}) => {
  const [archiveEvents, { loading: archiving }] = useMutation(
    archiveEventsMutation
  );
  const [unarchiveEvents, { loading: unarchiving }] = useMutation(
    unarchiveEventsMutation
  );

  const loading = archiving || unarchiving;

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (archived) {
        await unarchiveEvents({
          variables: { input: { ids: [eventId] } },
        });
      } else {
        await archiveEvents({
          variables: { input: { ids: [eventId] } },
        });
      }
      onComplete?.();
    },
    [archived, archiveEvents, unarchiveEvents, eventId, onComplete]
  );

  return (
    <Tooltip title={archived ? 'Unarchive' : 'Archive'}>
      <IconButton
        size="small"
        color="inherit"
        disabled={loading}
        onClick={handleClick}
        aria-label={archived ? 'Unarchive' : 'Archive'}
        sx={{ color: 'text.secondary' }}
      >
        {archived ? (
          <UnarchiveOutlinedIcon fontSize="small" />
        ) : (
          <ArchiveOutlinedIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};
